import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Pause, Play, Plus, RotateCcw, Timer, Trash2, Undo2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

// Featured/Sponsored advertisements (property, recruitment, event, developer, other).
// Featured Professionals are managed in their own tab and are excluded here.
const CATEGORIES = ["ALL", "PROPERTY", "RECRUITMENT", "EVENT", "DEVELOPER", "FINANCING", "PROFESSIONAL", "OTHER"];
const LABEL = { ALL: "All", PROPERTY: "Property", RECRUITMENT: "Recruitment", EVENT: "Events", DEVELOPER: "Developer",
  FINANCING: "Financing", PROFESSIONAL: "Professional", OTHER: "Other" };
const LIVE_STATES = new Set(["LIVE", "SCHEDULED", "ACTIVE", "SYSTEM_PAUSED", "PAUSED", "INACTIVE", "BLOCKED", "APPROVED"]);
const TONE = {
  LIVE: "bg-emerald-50 text-emerald-700", ACTIVE: "bg-emerald-50 text-emerald-700", SCHEDULED: "bg-sky-50 text-sky-700",
  PAUSED: "bg-amber-50 text-amber-700", SYSTEM_PAUSED: "bg-amber-50 text-amber-700", REMOVED: "bg-slate-100 text-slate-600",
  EXPIRED: "bg-slate-100 text-slate-500", COMPLETED: "bg-slate-100 text-slate-500", CANCELLED: "bg-rose-50 text-rose-600",
  INACTIVE: "bg-slate-100 text-slate-500", BLOCKED: "bg-rose-50 text-rose-600",
};

const manilaDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-CA", { timeZone: "Asia/Manila" }); } catch { return ""; }
};

function StatusPill({ s }) {
  return <span data-testid="sa-status" className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${TONE[s] || "bg-slate-100 text-slate-600"}`}>{s}</span>;
}

function ScheduleDialog({ ad, onClose, onSaved }) {
  const [start, setStart] = useState(manilaDate(ad?.starts_at));
  const [end, setEnd] = useState(manilaDate(ad?.ends_at));
  const [busy, setBusy] = useState(false);
  useEffect(() => { setStart(manilaDate(ad?.starts_at)); setEnd(manilaDate(ad?.ends_at)); }, [ad]);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/ad-campaigns/${ad.id}/schedule`, { start_date: start || null, end_date: end || null });
      toast.success("Schedule updated"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!ad} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="sa-schedule-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit placement dates</DialogTitle>
          <DialogDescription>Asia/Manila dates. Leave a field blank for no start or no end limit.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs font-semibold text-slate-500">Start
            <Input type="date" data-testid="sa-schedule-start" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1" />
          </label>
          <label className="text-xs font-semibold text-slate-500">End (inclusive)
            <Input type="date" data-testid="sa-schedule-end" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="sa-schedule-save" disabled={busy} onClick={save} className="bg-[#0F2C59] text-white hover:bg-[#143163]">Save dates</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ ad, onClose, onDeleted }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => setTyped(""), [ad]);
  const del = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/ad-campaigns/${ad.id}`, { params: { confirm: typed.trim() } });
      toast.success("Campaign permanently deleted (archived copy retained)"); onDeleted();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!ad} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="sa-delete-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Permanently delete campaign?</DialogTitle>
          <DialogDescription>
            This removes the campaign record. An archived copy is kept for audit and its analytics history stays intact.
            To only stop it showing, use Pause or Remove from placement instead. Type <span className="font-mono font-semibold">{ad?.id}</span> to confirm.
          </DialogDescription>
        </DialogHeader>
        <Input data-testid="sa-delete-confirm-input" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Campaign ID" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="sa-delete-confirm" disabled={busy || typed.trim() !== ad?.id} onClick={del} className="bg-rose-600 text-white hover:bg-rose-700">Delete permanently</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Act({ testid, onClick, icon: Icon, children, danger, disabled }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick} disabled={disabled}
      className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-[11px] font-semibold disabled:opacity-50 ${danger ? "border-rose-200 text-rose-600 hover:bg-rose-50" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
      <Icon className="h-3.5 w-3.5" />{children}
    </button>
  );
}

function LegacyActions({ ad, run, onSchedule, onDelete }) {
  const s = ad.status;
  return (
    <div className="flex flex-wrap gap-1.5">
      {["LIVE", "SCHEDULED", "INACTIVE", "BLOCKED"].includes(s) && <Act testid={`sa-pause-${ad.id}`} icon={Pause} onClick={() => run(ad, "pause")}>Pause</Act>}
      {s === "PAUSED" && <Act testid={`sa-resume-${ad.id}`} icon={Play} onClick={() => run(ad, "resume")}>Resume</Act>}
      {s === "REMOVED" && <Act testid={`sa-restore-${ad.id}`} icon={Undo2} onClick={() => run(ad, "resume")}>Restore to placement</Act>}
      {s !== "REMOVED" && <Act testid={`sa-remove-${ad.id}`} icon={RotateCcw} onClick={() => run(ad, "remove-placement", `Remove "${ad.campaign_name}" from ${ad.placement}? The campaign and its analytics are kept.`)}>Remove from placement</Act>}
      {s !== "REMOVED" && <Act testid={`sa-schedule-${ad.id}`} icon={CalendarDays} onClick={() => onSchedule(ad)}>Edit dates</Act>}
      {!["EXPIRED", "REMOVED"].includes(s) && <Act testid={`sa-expire-${ad.id}`} icon={Timer} onClick={() => run(ad, "expire", `Expire "${ad.campaign_name}" now? It stops showing immediately.`)}>Expire</Act>}
      <Act testid={`sa-delete-${ad.id}`} icon={Trash2} danger onClick={() => onDelete(ad)}>Delete</Act>
    </div>
  );
}

function ManagedActions({ ad, runManaged }) {
  const s = ad.status;
  return (
    <div className="flex flex-wrap gap-1.5">
      {["SCHEDULED", "ACTIVE"].includes(s) && <Act testid={`sa-mpause-${ad.id}`} icon={Pause} onClick={() => runManaged(ad, "pause")}>Pause</Act>}
      {s === "SYSTEM_PAUSED" && <Act testid={`sa-mresume-${ad.id}`} icon={Play} onClick={() => runManaged(ad, "resume")}>Resume</Act>}
      {["APPROVED", "SCHEDULED", "ACTIVE", "SYSTEM_PAUSED"].includes(s) && <Act testid={`sa-mcancel-${ad.id}`} icon={Timer} onClick={() => runManaged(ad, "cancel", `Cancel "${ad.campaign_name}"? It stops delivering and its reservation is released.`)}>End campaign</Act>}
      {!["APPROVED", "SCHEDULED", "ACTIVE", "SYSTEM_PAUSED"].includes(s) && <span className="text-[11px] text-slate-400">No actions</span>}
    </div>
  );
}

function AdRow({ ad, run, runManaged, onSchedule, onDelete }) {
  const win = ad.kind === "MANAGED"
    ? (ad.start_date ? `${ad.start_date} · ${ad.duration_days}d` : "—")
    : `${manilaDate(ad.starts_at) || "Now"} → ${manilaDate(ad.ends_at) || "No end"}`;
  return (
    <tr className="border-b border-slate-100 align-top" data-testid={`sa-row-${ad.id}`}>
      <td className="px-3 py-3">
        <div className="flex items-start gap-3 min-w-[220px]">
          {ad.image ? <img src={ad.image} alt="" className="h-12 w-16 shrink-0 rounded-md object-cover bg-slate-100" /> : <div className="h-12 w-16 shrink-0 rounded-md bg-slate-100" />}
          <div className="min-w-0">
            <div className="font-semibold text-slate-900 line-clamp-2">{ad.campaign_name}</div>
            <div className="text-[11px] text-slate-500">{ad.disclosure} · {ad.advertiser}</div>
            <div className="text-[10px] text-slate-400 font-mono">{ad.id} · {ad.kind === "LEGACY" ? "Admin-managed" : "Lifecycle-managed"}</div>
          </div>
        </div>
      </td>
      <td className="px-3 py-3 text-xs"><span className="rounded-full bg-[#0F2C59]/5 px-2 py-0.5 font-semibold text-[#0F2C59]">{LABEL[ad.category] || ad.category}</span></td>
      <td className="px-3 py-3 text-[11px] font-semibold text-[#0F2C59]">{ad.placement}</td>
      <td className="px-3 py-3 whitespace-nowrap text-xs text-slate-500">{win}</td>
      <td className="px-3 py-3"><StatusPill s={ad.status} /></td>
      <td className="px-3 py-3 whitespace-nowrap text-xs text-slate-600" data-testid={`sa-metrics-${ad.id}`}>{ad.metrics.impressions} imp · {ad.metrics.clicks} clicks · {ad.metrics.ctr}%</td>
      <td className="px-3 py-3">
        {ad.kind === "LEGACY"
          ? <LegacyActions ad={ad} run={run} onSchedule={onSchedule} onDelete={onDelete} />
          : <ManagedActions ad={ad} runManaged={runManaged} />}
      </td>
    </tr>
  );
}

export default function SponsoredAdsAdmin() {
  const [items, setItems] = useState(null);
  const [cat, setCat] = useState("ALL");
  const [show, setShow] = useState("CURRENT");
  const [placement, setPlacement] = useState("ALL");
  const [kind, setKind] = useState("ALL");
  const [schedAd, setSchedAd] = useState(null);
  const [delAd, setDelAd] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/sponsored-ads").then((r) => setItems(r.data.items || [])).catch((e) => { toast.error(apiError(e)); setItems([]); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (ad, action, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    try { await api.post(`/admin/ad-campaigns/${ad.id}/${action}`); toast.success("Updated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const runManaged = async (ad, action, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    try { await api.post(`/admin/campaigns/${ad.id}/${action}`); toast.success("Updated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const placements = useMemo(() => ["ALL", ...Array.from(new Set((items || []).map((i) => i.placement))).sort()], [items]);
  const RANK = { LIVE: 0, ACTIVE: 0, SCHEDULED: 1, PAUSED: 2, SYSTEM_PAUSED: 2, INACTIVE: 3, BLOCKED: 3 };
  const rows = (items || []).filter((i) =>
    (cat === "ALL" || i.category === cat)
    && (placement === "ALL" || i.placement === placement)
    && (kind === "ALL" || i.kind === kind)
    && (show === "ALL" || (show === "CURRENT" ? LIVE_STATES.has(i.status) : !LIVE_STATES.has(i.status))))
    // Admin-managed (house) ads first, then by delivery status so what's visible publicly is on top.
    .sort((a, b) => (a.kind === b.kind ? 0 : a.kind === "LEGACY" ? -1 : 1) || ((RANK[a.status] ?? 9) - (RANK[b.status] ?? 9)));
  const liveCount = (items || []).filter((i) => i.status === "LIVE" || i.status === "ACTIVE").length;

  return (
    <div className="space-y-4" data-testid="sponsored-ads-admin">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-bold text-[#0F2C59]">Featured & Sponsored Advertisements</h3>
          <p className="text-sm text-slate-500">Property, recruitment, event, developer and partner ads. Featured Professionals are managed in their own tab.</p>
          <p className="mt-1 text-xs font-semibold text-emerald-700" data-testid="sa-live-count">{liveCount} delivering now</p>
        </div>
        <Link to="/admin/spotlight" data-testid="sa-create-link" className="inline-flex items-center gap-1.5 rounded-xl bg-[#0F2C59] px-3 py-2 text-sm font-semibold text-white hover:bg-[#143163]">
          <Plus className="h-4 w-4" /> Create advertisement
        </Link>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {CATEGORIES.map((c) => (
          <button key={c} data-testid={`sa-cat-${c}`} onClick={() => setCat(c)}
            className={`rounded-full border px-3 py-1.5 text-xs ${cat === c ? "border-[#0F2C59] bg-[#0F2C59] text-white" : "border-slate-200 bg-white text-slate-600"}`}>{LABEL[c]}</button>
        ))}
        <select data-testid="sa-show-filter" value={show} onChange={(e) => setShow(e.target.value)} className="ml-auto h-9 rounded-lg border border-slate-200 bg-white px-2 text-xs">
          <option value="CURRENT">Current (live, scheduled, paused)</option>
          <option value="ENDED">Ended / removed</option>
          <option value="ALL">All</option>
        </select>
        <select data-testid="sa-kind-filter" value={kind} onChange={(e) => setKind(e.target.value)} className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-xs">
          <option value="ALL">All sources</option>
          <option value="LEGACY">Admin-managed ads</option>
          <option value="MANAGED">Lifecycle-managed campaigns</option>
        </select>
        <select data-testid="sa-placement-filter" value={placement} onChange={(e) => setPlacement(e.target.value)} className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-xs">
          {placements.map((p) => <option key={p} value={p}>{p === "ALL" ? "All placements" : p}</option>)}
        </select>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="sa-table">
          <thead><tr className="border-b border-slate-200 text-left text-[11px] uppercase text-slate-400">
            <th className="px-3 py-2">Advertisement</th><th className="px-3 py-2">Category</th><th className="px-3 py-2">Placement</th>
            <th className="px-3 py-2">Duration</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Analytics</th><th className="px-3 py-2">Actions</th>
          </tr></thead>
          <tbody>{rows.map((ad) => <AdRow key={ad.id} ad={ad} run={run} runManaged={runManaged} onSchedule={setSchedAd} onDelete={setDelAd} />)}</tbody>
        </table>
        {items && rows.length === 0 && <div className="p-6 text-center text-sm text-slate-500" data-testid="sa-empty">No advertisements match these filters.</div>}
        {!items && <div className="p-6 text-center text-sm text-slate-500">Loading…</div>}
      </div>
      <ScheduleDialog ad={schedAd} onClose={() => setSchedAd(null)} onSaved={() => { setSchedAd(null); load(); }} />
      <DeleteDialog ad={delAd} onClose={() => setDelAd(null)} onDeleted={() => { setDelAd(null); load(); }} />
    </div>
  );
}
